import type { AIProviderCode } from './ai-providers.ts';

// =============================================================================
// Per-model pricing (USD per 1M tokens) — mirrors edge function pricing table
// =============================================================================

export interface ModelPricing {
  inputCostPerMillion: number;
  outputCostPerMillion: number;
}

/**
 * Pricing per provider and model. Updated as of Feb 2026.
 * Keep in sync with `supabase/functions/_shared/ai-providers/pricing.ts`.
 */
export const MODEL_PRICING: Record<string, Record<string, ModelPricing>> = {
  anthropic: {
    'claude-sonnet-4-5-20250929': { inputCostPerMillion: 3, outputCostPerMillion: 15 },
    'claude-haiku-4-5-20251001': { inputCostPerMillion: 1, outputCostPerMillion: 5 },
  },
  openai: {
    'gpt-4o': { inputCostPerMillion: 2.5, outputCostPerMillion: 10 },
    'gpt-4o-mini': { inputCostPerMillion: 0.15, outputCostPerMillion: 0.6 },
    'gpt-5': { inputCostPerMillion: 1.25, outputCostPerMillion: 10 },
    'gpt-5-mini': { inputCostPerMillion: 0.25, outputCostPerMillion: 2 },
    'gpt-5-nano': { inputCostPerMillion: 0.05, outputCostPerMillion: 0.4 },
  },
  gemini: {
    'gemini-2.0-flash': { inputCostPerMillion: 0.1, outputCostPerMillion: 0.4 },
    'gemini-2.5-flash': { inputCostPerMillion: 0.15, outputCostPerMillion: 0.6 },
  },
};

/** Fallback pricing if the model isn't in our pricing table. */
const FALLBACK_PRICING: ModelPricing = {
  inputCostPerMillion: 3,
  outputCostPerMillion: 15,
};

/**
 * Looks up pricing for a provider/model combo.
 * Returns fallback (Anthropic-level) pricing if the exact model isn't listed.
 */
export function getModelPricing(provider: AIProviderCode, model: string): ModelPricing {
  return MODEL_PRICING[provider]?.[model] ?? FALLBACK_PRICING;
}
